import type { TranslationCatalog } from './api-translate.loader';

/**
 * Persistance localStorage du catalogue public multi-locale.
 *
 * But : servir les traductions instantanément au boot (sans attendre l'API) et
 * ne déclencher qu'une revalidation légère en tâche de fond. Helpers volontairement
 * purs (aucune dépendance Angular) : le loader i18n les appelle pendant la
 * construction de TranslateService, où toute injection de service crée un cycle
 * NG0200 (cf. api-translate.loader.ts).
 */

/** Clé versionnée : bumper le suffixe invalide les anciens formats de cache. */
const CACHE_KEY = 'i18n_catalog_v1';

/** Catalogue public toutes-locales mémorisé en localStorage. */
export interface CachedCatalog {
  /** `generated_at` renvoyé par l'API : sert de clé de fraîcheur. */
  generatedAt: string;
  /** Un catalogue imbriqué par code langue (`fr`, `en`, …). */
  locales: Record<string, TranslationCatalog>;
}

/** Lit le cache ; retourne `null` si absent, illisible ou corrompu. */
export function readCache(): CachedCatalog | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as CachedCatalog;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof parsed.generatedAt !== 'string' ||
      typeof parsed.locales !== 'object' ||
      parsed.locales === null
    ) {
      return null;
    }
    return parsed;
  } catch {
    // JSON corrompu / storage indisponible : on repart sans cache.
    return null;
  }
}

/** Écrit le cache. Silencieux en cas d'échec (quota, storage désactivé). */
export function writeCache(catalog: CachedCatalog): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(catalog));
  } catch {
    // Le cache est un bonus de perf : un échec d'écriture n'est pas bloquant.
  }
}

/** Purge le cache (utile pour les tests et un éventuel reset explicite). */
export function clearCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}
